import React, { useState, useEffect } from 'react';
import { useLang } from '@shootout-comps/i18n';
import { LANG_OPTIONS } from '@shootout-comps/i18n';
import type { LangCode } from '@shootout-comps/i18n';
import SnookerBall, { } from '../components/SnookerBall';

interface SplashPageProps {
  onSignIn: () => void;
  onSignUp: () => void;
  onPrivacy?: () => void;
}

const features = [
  { icon: '🏆', key: 'bracket'    as const },
  { icon: '🔴', key: 'scoring'    as const },
  { icon: '⏱',  key: 'timer'      as const },
  { icon: '📊', key: 'history'    as const },
  { icon: '👥', key: 'players'    as const },
  { icon: '🖨',  key: 'poster'     as const },
  { icon: '📱', key: 'mobile'     as const },
];

const FEATURE_TITLES: Record<string, Record<string, string>> = {
  bracket:  { en: 'Automatic Bracket Draw', es: 'Sorteo automático', fr: 'Tirage au sort automatique', de: 'Automatische Auslosung', pt: 'Chave automática', it: 'Tabellone automatico', ja: '自動抽選', zh: '自动签表抽签', ar: 'قرعة تلقائية', hi: 'स्वचालित ब्रैकेट ड्रॉ' },
  scoring:  { en: 'Real-Time Scoring',   es: 'Puntuación en tiempo real',   fr: 'Score en temps réel',   de: 'Echtzeit-Ergebnisse',    pt: 'Pontuação em tempo real',   it: 'Punteggio in tempo reale', ja: 'リアルタイムスコア', zh: '实时计分', ar: 'التسجيل الفوري', hi: 'रियल-टाइम स्कोरिंग' },
  timer:    { en: 'Three Match Formats', es: 'Tres formatos de partido', fr: 'Trois formats de match', de: 'Drei Spielformate', pt: 'Três formatos de jogo', it: 'Tre formati di partita', ja: '3つの試合形式', zh: '三种比赛格式', ar: 'ثلاث صيغ للمباراة', hi: 'तीन मैच फॉर्मेट' },
  history:  { en: 'Full Tournament History', es: 'Historial completo',      fr: 'Historique complet',    de: 'Vollständige Turnierhistorie', pt: 'Histórico completo',    it: 'Storico completo', ja: '完全な履歴', zh: '完整历史记录', ar: 'سجل كامل', hi: 'पूर्ण टूर्नामेंट इतिहास' },
  players:  { en: 'Player Profiles & Photos', es: 'Perfiles de jugadores', fr: 'Profils de joueurs',    de: 'Spielerprofile',          pt: 'Perfis de jogadores',      it: 'Profili giocatori', ja: '選手プロフィール', zh: '球员档案和照片', ar: 'ملفات اللاعبين', hi: 'खिलाड़ी प्रोफाइल' },
  poster:   { en: 'PDF Poster Generator', es: 'Generador de carteles PDF', fr: 'Générateur d\'affiches PDF', de: 'PDF-Poster-Generator', pt: 'Gerador de cartazes PDF', it: 'Generatore poster PDF', ja: 'PDFポスター生成', zh: 'PDF海报生成器', ar: 'مولد ملصقات PDF', hi: 'PDF पोस्टर जनरेटर' },
  mobile:   { en: 'Mobile Friendly',    es: 'Optimizado para móvil',        fr: 'Compatible mobile',    de: 'Mobilgeräte-freundlich',  pt: 'Compatível com mobile',     it: 'Ottimizzato per mobile', ja: 'モバイル対応', zh: '移动端友好', ar: 'متوافق مع الجوال', hi: 'मोबाइल फ्रेंडली' },
};

const FEATURE_DESCS: Record<string, Record<string, string>> = {
  bracket:  { en: 'Automatic draws up to 128 players with bye handling', es: 'Sorteos automáticos para hasta 128 jugadores', fr: 'Tirages automatiques jusqu\'à 128 joueurs', de: 'Automatische Auslosung bis 128 Spieler', pt: 'Sorteios automáticos para até 128 jogadores', it: 'Sorteggi automatici fino a 128 giocatori', ja: '最大128名の自動抽選', zh: '最多128名球员的自动签表', ar: 'قرعة تلقائية لما يصل إلى 128 لاعباً', hi: 'अधिकतम 128 खिलाड़ियों के साथ स्वचालित सीडेड ड्रॉ' },
  scoring:  { en: 'Live ball-by-ball scoring for every match format', es: 'Puntuación bola a bola en tiempo real', fr: 'Score bille par bille en temps réel', de: 'Live Ball-für-Ball Ergebnisse', pt: 'Pontuação bola a bola ao vivo', it: 'Punteggio palla per palla in diretta', ja: 'リアルタイムのボールごとのスコア', zh: '实时逐球计分', ar: 'التسجيل الحي كرة بكرة', hi: 'लाइव बॉल-बाई-बॉल स्कोरिंग' },
  timer:    { en: 'Shootout (10 min · 15s/10s shot clock), Snooker 900 (15 min · 20s shot clock) or untimed Standard best-of-frames matches', es: 'Shootout (10 min · reloj de tiro 15s/10s), Snooker 900 (15 min · reloj de tiro 20s) o partidos Standard sin límite de tiempo a mejor de frames', fr: 'Shootout (10 min · chronomètre 15s/10s), Snooker 900 (15 min · chronomètre 20s) ou matchs Standard au meilleur des manches, sans chronomètre', de: 'Shootout (10 Min. · Schussuhr 15s/10s), Snooker 900 (15 Min. · Schussuhr 20s) oder zeitlose Standard-Spiele auf mehrere Gewinnsätze', pt: 'Shootout (10 min · relógio de tacada 15s/10s), Snooker 900 (15 min · relógio de tacada 20s) ou partidas Standard sem cronômetro, em melhor de frames', it: 'Shootout (10 min · orologio 15s/10s), Snooker 900 (15 min · orologio 20s) o partite Standard senza limite di tempo al meglio dei frame', ja: 'シュートアウト（10分・ショットクロック15秒/10秒）、スヌーカー900（15分・ショットクロック20秒）、または時間無制限のスタンダード（フレーム制）', zh: 'Shootout（10分钟，击球时钟15秒/10秒）、Snooker 900（15分钟，击球时钟20秒）或不限时的Standard多局比赛', ar: 'Shootout (10 دقائق · ساعة ضربة 15/10 ثانية)، Snooker 900 (15 دقيقة · ساعة ضربة 20 ثانية)، أو مباريات Standard غير محددة الوقت بنظام الأفضل من عدة فريمات', hi: 'Shootout (10 मिनट · 15/10 सेकंड शॉट क्लॉक), Snooker 900 (15 मिनट · 20 सेकंड शॉट क्लॉक), या असीमित समय वाले Standard बेस्ट-ऑफ-फ्रेम मैच' },
  history:  { en: 'View past brackets, every match result and detailed player statistics', es: 'Ver cuadros anteriores, resultados de partidos y estadísticas detalladas de jugadores', fr: 'Consultez les tableaux précédents, les résultats des matchs et les statistiques détaillées des joueurs', de: 'Frühere Turnierbäume, Spielergebnisse und detaillierte Spielerstatistiken ansehen', pt: 'Veja chaveamentos anteriores, resultados das partidas e estatísticas detalhadas dos jogadores', it: 'Visualizza tabelloni passati, risultati delle partite e statistiche dettagliate dei giocatori', ja: '過去の組み合わせ、試合結果、詳細な選手統計を確認', zh: '查看历史签表、比赛结果和详细的球员数据统计', ar: 'عرض القرعات السابقة ونتائج المباريات وإحصاءات اللاعبين التفصيلية', hi: 'पिछले ड्रॉ, मैच परिणाम और विस्तृत खिलाड़ी आंकड़े देखें' },
  players:  { en: 'Profile photos and player statistics', es: 'Fotos de perfil y estadísticas de jugadores', fr: 'Photos de profil et statistiques', de: 'Profilfotos und Spielerstatistiken', pt: 'Fotos de perfil e estatísticas', it: 'Foto profilo e statistiche giocatori', ja: 'プロフィール写真と統計', zh: '球员头像和数据统计', ar: 'صور الملف الشخصي وإحصاءات اللاعبين', hi: 'प्रोफाइल फोटो और आंकड़े' },
  poster:   { en: 'Auto-generate a professional A4 tournament poster as a PDF — includes venue, entry fee, prize breakdown and format', es: 'Genera automáticamente un cartel de torneo en PDF con lugar, cuota y premios', fr: "Générez automatiquement une affiche de tournoi A4 en PDF avec lieu, frais et prix", de: 'Erstelle automatisch ein A4-Turnierplakat als PDF mit Ort, Startgebühr und Preisen', pt: 'Gere automaticamente um cartaz de torneio A4 em PDF com local, taxa e prémios', it: 'Genera automaticamente un poster A4 del torneo in PDF con luogo, quota e premi', ja: '会場・参加費・賞金を含むA4トーナメントポスターをPDFで自動生成', zh: '自动生成包含场地、报名费和奖金的A4赛事海报PDF', ar: 'أنشئ تلقائياً ملصق بطولة A4 بتنسيق PDF يتضمن المكان والرسوم والجوائز', hi: 'A4 टूर्नामेंट पोस्टर PDF में स्वचालित रूप से बनाएं' },
  mobile:   { en: 'Manage matches from the side of any table on any device', es: 'Gestiona desde cualquier dispositivo', fr: 'Gérez depuis n\'importe quel appareil', de: 'Von jedem Gerät verwalten', pt: 'Gerencie de qualquer dispositivo', it: 'Gestisci da qualsiasi dispositivo', ja: 'どのデバイスからでも管理', zh: '在任何设备上管理比赛', ar: 'إدارة من أي جهاز', hi: 'किसी भी डिवाइस से प्रबंधन' },
};

export default function SplashPage({ onSignIn, onSignUp, onPrivacy }: SplashPageProps) {
  const { t, lang, setLang } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const [freeTournamentsCount, setFreeTournamentsCount] = useState<number | null>(null);
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);
  const [timedPricePence, setTimedPricePence] = useState(500);

  useEffect(() => {
    fetch('/api/settings/public', { headers: { 'Cache-Control': 'no-cache' } })
      .then(r => r.json())
      .then((d: { standardTournamentsEnabled?: boolean; freeTournamentsCount?: number; paymentsEnabled?: boolean; timedPricePence?: number }) => {
        setFreeTournamentsCount(typeof d.freeTournamentsCount === 'number' ? d.freeTournamentsCount : 1);
        setPaymentsEnabled(typeof d.paymentsEnabled === 'boolean' ? d.paymentsEnabled : true);
        if (typeof d.timedPricePence === 'number') setTimedPricePence(d.timedPricePence);
      })
      .catch(() => { setFreeTournamentsCount(1); setPaymentsEnabled(true); });
  }, []);

  // Derive display strings from admin settings — all use translation keys with {n}/{price} placeholders
  const priceStr = `£${(timedPricePence / 100).toFixed(2)}`;

  const pricingText = (() => {
    if (freeTournamentsCount === null) return null;
    if (!paymentsEnabled) return t.splashPricingAllFree;
    if (freeTournamentsCount === 0) return t.splashPricingPaidOnly.replace('{price}', priceStr);
    if (freeTournamentsCount === 1) return t.splashPricingOneFree.replace('{price}', priceStr);
    return t.splashPricingNFree.replace('{n}', String(freeTournamentsCount)).replace('{price}', priceStr);
  })();

  const dynamicSubtitle = (() => {
    if (freeTournamentsCount === null) return t.splashSubtitle;
    if (!paymentsEnabled) return t.splashSubtitleAllFree;
    if (freeTournamentsCount === 0) return t.splashSubtitlePaid;
    if (freeTournamentsCount === 1) return t.splashSubtitle;
    return t.splashSubtitleNFree.replace('{n}', String(freeTournamentsCount));
  })();

  const dynamicNoCreditCard = (() => {
    if (freeTournamentsCount === null) return t.splashNoCreditCard;
    if (!paymentsEnabled) return t.splashNoCreditCardAllFree;
    if (freeTournamentsCount === 0) return t.splashNoCreditCardPaid;
    if (freeTournamentsCount === 1) return t.splashNoCreditCard;
    return t.splashNoCreditCardNFree.replace('{n}', String(freeTournamentsCount));
  })();

  const dynamicStep1Desc = (() => {
    if (freeTournamentsCount === null) return t.splashStep1Desc;
    if (!paymentsEnabled) return t.splashStep1DescAllFree;
    if (freeTournamentsCount === 0) return t.splashStep1DescPaid;
    if (freeTournamentsCount === 1) return t.splashStep1Desc;
    return t.splashStep1DescNFree.replace('{n}', String(freeTournamentsCount));
  })();

  const dynamicGetStarted = (!paymentsEnabled || (freeTournamentsCount ?? 1) > 0)
    ? t.splashGetStarted
    : t.splashGetStartedPaid;

  const ft = (key: string) => FEATURE_TITLES[key]?.[lang] ?? FEATURE_TITLES[key]?.['en'] ?? key;
  const fd = (key: string) => FEATURE_DESCS[key]?.[lang]  ?? FEATURE_DESCS[key]?.['en']  ?? key;

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--baize-dark)', overflowX: 'hidden' }}>

      {/* ── Nav ─────────────────────────────────────────────────────────────── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'linear-gradient(180deg, var(--rail), var(--rail-light))',
        borderBottom: '3px solid var(--gold)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 clamp(1rem,4vw,2.5rem)', height: '60px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <SnookerBall size={24} />
          <span style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1rem,4vw,1.4rem)', color: 'var(--gold)', letterSpacing: '0.04em' }}>Shootout Comps</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* Language picker */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '0.3rem 0.5rem', fontSize: '0.9rem', cursor: 'pointer', color: 'var(--chalk)', minHeight: '34px' }}>
              {LANG_OPTIONS.find(l => l.code === lang)?.flag ?? '🌐'}
            </button>
            {menuOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: 'var(--rail)', border: '1px solid var(--gold)', borderRadius: '8px', overflow: 'hidden', zIndex: 200, minWidth: '150px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                {LANG_OPTIONS.map(({ code, name, flag }) => (
                  <button key={code} onClick={() => { setLang(code as LangCode); setMenuOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.55rem 0.85rem', background: code === lang ? 'rgba(201,168,76,0.15)' : 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)', color: code === lang ? 'var(--gold)' : 'var(--chalk)', fontSize: '0.8rem', cursor: 'pointer', minHeight: '38px', textAlign: 'start' }}>
                    <span>{flag}</span><span>{name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={onSignIn} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', padding: '0.35rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', minHeight: '34px' }}>
            {t.splashSignIn}
          </button>
          <button onClick={onSignUp} style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '6px', padding: '0.35rem 0.9rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', minHeight: '34px' }}>
            {t.splashSignUp}
          </button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section style={{
        padding: 'clamp(3rem,10vw,7rem) clamp(1rem,5vw,2.5rem)',
        textAlign: 'center',
        background: `
          radial-gradient(ellipse 80% 60% at 50% 0%, rgba(201,168,76,0.08) 0%, transparent 70%),
          radial-gradient(ellipse 60% 40% at 80% 100%, rgba(35,96,56,0.4) 0%, transparent 60%)
        `,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative billiard balls */}
        {[
          { c: '#c0392b', x: '8%',  y: '20%', s: 44 },
          { c: '#f1c40f', x: '92%', y: '15%', s: 36 },
          { c: '#2980b9', x: '5%',  y: '75%', s: 52 },
          { c: '#e91e8c', x: '94%', y: '70%', s: 40 },
          { c: '#1a1a1a', x: '88%', y: '40%', s: 48 },
        ].map((b, i) => (
          <div key={i} style={{
            position: 'absolute', left: b.x, top: b.y,
            width: b.s, height: b.s, borderRadius: '50%',
            background: `radial-gradient(circle at 35% 35%, ${lighten(b.c)}, ${b.c})`,
            boxShadow: `0 4px 12px rgba(0,0,0,0.5), inset 0 1px 3px rgba(255,255,255,0.3)`,
            opacity: 0.35,
            animation: `floatBall${i} ${4 + i}s ease-in-out infinite`,
          }} />
        ))}

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '760px', margin: '0 auto' }}>
          <SnookerBall size={80} style={{ marginBottom: '0.5rem' }} />
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(2.4rem,9vw,5rem)', color: 'var(--gold)', lineHeight: 1, letterSpacing: '0.03em', marginBottom: '0.75rem' }}>
            Shootout Comps
          </h1>
          <p style={{ fontSize: 'clamp(1rem,3.5vw,1.4rem)', color: 'var(--chalk)', marginBottom: '0.5rem', fontWeight: 500 }}>
            {t.splashTagline}
          </p>
          <p style={{ fontSize: 'clamp(0.85rem,2.5vw,1.05rem)', color: 'var(--chalk-dim)', marginBottom: 'clamp(1.5rem,5vw,2.5rem)', maxWidth: '520px', margin: '0 auto clamp(1.5rem,5vw,2.5rem)', lineHeight: 1.6 }}>
            {dynamicSubtitle}
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={onSignUp} style={{
              background: 'linear-gradient(135deg, var(--gold), var(--gold-light))',
              color: 'var(--baize-dark)', border: 'none', borderRadius: '10px',
              padding: 'clamp(0.75rem,3vw,1rem) clamp(1.5rem,5vw,2.5rem)',
              fontSize: 'clamp(0.9rem,3vw,1.05rem)', fontWeight: 700,
              letterSpacing: '0.05em', cursor: 'pointer',
              boxShadow: '0 6px 24px rgba(201,168,76,0.35)',
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 10px 32px rgba(201,168,76,0.45)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 24px rgba(201,168,76,0.35)'; }}
            >
              {t.createAccount}
            </button>
            <button onClick={onSignIn} style={{
              background: 'rgba(255,255,255,0.06)', color: 'var(--chalk)',
              border: '1.5px solid rgba(255,255,255,0.2)', borderRadius: '10px',
              padding: 'clamp(0.75rem,3vw,1rem) clamp(1.5rem,5vw,2rem)',
              fontSize: 'clamp(0.9rem,3vw,1.05rem)', fontWeight: 500, cursor: 'pointer',
              transition: 'border-color 0.2s, background 0.2s',
            }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.5)'; (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.08)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.2)'; (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
            >
              {t.splashSignIn}
            </button>
          </div>
          <p style={{ marginTop: '1rem', color: 'var(--chalk-dim)', fontSize: '0.78rem' }}>
            {dynamicNoCreditCard}
          </p>
        </div>
      </section>

      {/* ── Features grid ───────────────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(2.5rem,8vw,5rem) clamp(1rem,5vw,2.5rem)', maxWidth: '1000px', margin: '0 auto' }}>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.6rem,5vw,2.4rem)', color: 'var(--gold)', textAlign: 'center', marginBottom: 'clamp(1.5rem,5vw,3rem)', letterSpacing: '0.03em' }}>
          {t.splashFeaturesHeading}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'clamp(0.75rem,3vw,1.25rem)' }}>
          {features.map(({ icon, key }) => (
            <div key={key} style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.055), rgba(255,255,255,0.02))',
              border: '1px solid rgba(255,255,255,0.09)',
              borderRadius: '12px', padding: 'clamp(1.25rem,4vw,1.75rem)',
              transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
            }}
              onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.transform = 'translateY(-3px)'; el.style.borderColor = 'rgba(201,168,76,0.3)'; el.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)'; }}
              onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.transform = ''; el.style.borderColor = 'rgba(255,255,255,0.09)'; el.style.boxShadow = ''; }}
            >
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>{icon}</div>
              <h3 style={{ color: 'var(--gold)', fontSize: 'clamp(0.95rem,2.5vw,1.05rem)', fontWeight: 700, marginBottom: '0.4rem' }}>{ft(key)}</h3>
              <p style={{ color: 'var(--chalk-dim)', fontSize: 'clamp(0.78rem,2vw,0.85rem)', lineHeight: 1.6, margin: 0 }}>{fd(key)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(2rem,6vw,4rem) clamp(1rem,5vw,2.5rem)', background: 'rgba(0,0,0,0.2)' }}>
        <div style={{ maxWidth: '680px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.5rem,5vw,2.2rem)', color: 'var(--gold)', marginBottom: 'clamp(1.5rem,5vw,2.5rem)', letterSpacing: '0.03em' }}>
            {t.splashUpAndRunning}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { n: '1', t: t.splashStep1Title, d: dynamicStep1Desc },
              { n: '2', t: t.splashStep2Title, d: t.splashStep2Desc },
              { n: '3', t: t.splashStep3Title, d: t.splashStep3Desc },
              { n: '4', t: t.splashStep4Title, d: t.splashStep4Desc },
            ].map(step => (
              <div key={step.n} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '1rem 1.25rem', textAlign: 'start' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', fontFamily: 'Bebas Neue', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{step.n}</div>
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--chalk)', marginBottom: '0.2rem', fontSize: 'clamp(0.88rem,2.5vw,0.95rem)' }}>{step.t}</div>
                  <div style={{ color: 'var(--chalk-dim)', fontSize: 'clamp(0.78rem,2vw,0.83rem)' }}>{step.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(2.5rem,8vw,5rem) clamp(1rem,5vw,2.5rem)', textAlign: 'center' }}>
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          <SnookerBall size={48} style={{ marginBottom: '0.75rem' }} />
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.6rem,6vw,2.5rem)', color: 'var(--gold)', marginBottom: '0.75rem', letterSpacing: '0.03em' }}>
            {t.splashReadyTitle}
          </h2>
          <p style={{ color: 'var(--chalk-dim)', marginBottom: '1.75rem', lineHeight: 1.6, fontSize: 'clamp(0.85rem,2.5vw,0.95rem)' }}>
            {pricingText}
          </p>
          <button onClick={onSignUp} style={{
            background: 'linear-gradient(135deg, var(--gold), var(--gold-light))',
            color: 'var(--baize-dark)', border: 'none', borderRadius: '10px',
            padding: 'clamp(0.85rem,3vw,1.1rem) clamp(2rem,6vw,3rem)',
            fontSize: 'clamp(0.95rem,3vw,1.1rem)', fontWeight: 700,
            letterSpacing: '0.06em', cursor: 'pointer',
            boxShadow: '0 6px 24px rgba(201,168,76,0.35)',
          }}>
            {dynamicGetStarted}
          </button>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.07)', padding: '1.25rem clamp(1rem,4vw,2.5rem)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem' }}>{t.splashFooterTagline}</span>
          <button onClick={() => onPrivacy?.()} style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontSize: '0.78rem', textDecoration: 'underline', padding: 0 }}>
            {t.privacyPolicy}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {LANG_OPTIONS.map(({ code, flag }) => (
            <button key={code} onClick={() => setLang(code as LangCode)}
              title={LANG_OPTIONS.find(l => l.code === code)?.name}
              style={{ background: code === lang ? 'rgba(201,168,76,0.15)' : 'transparent', border: `1px solid ${code === lang ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '4px', padding: '0.2rem 0.4rem', fontSize: '0.9rem', cursor: 'pointer', lineHeight: 1 }}>
              {flag}
            </button>
          ))}
        </div>
      </footer>

      <style>{`
        @keyframes floatBall0 { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-12px) rotate(5deg)} }
        @keyframes floatBall1 { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-8px)  rotate(-4deg)} }
        @keyframes floatBall2 { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-15px) rotate(7deg)} }
        @keyframes floatBall3 { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-10px) rotate(-6deg)} }
        @keyframes floatBall4 { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-7px)  rotate(3deg)} }
      `}</style>
    </div>
  );
}

function lighten(hex: string): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, ((n >> 16) & 0xff) + 50);
  const g = Math.min(255, ((n >> 8) & 0xff) + 50);
  const b = Math.min(255, (n & 0xff) + 50);
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}
